import { PHASE } from '../../domain/operations.js';
import { GROUND_FRAMES } from '../../domain/gateflow.js';
import { boardingWindowOpen, earlyBoardingWindowOpen, ensureTimetable, recordDeparture, scheduleTurnaround,
    BOARDING_SHORTLY_FRAMES, BOARDING_DESK_FRAMES, DEPARTURE_COUNTDOWN, AIRPORT_MINUTE } from '../../domain/timetable.js';
import { departureTripFrames } from '../../domain/flight-duration.js';
import { randomInteger } from './random.js';
import type { FlowFacts, FlowPassenger, FlowState, InquiryPassenger } from './types.js';

const blockedByDeplaning = (state: FlowState, gate: number) => state.inbound.some(q => q.gate === gate && q.gateFrames > 0);

/** Questions, readiness and the boarding call follow the live automaticBoarding rules at one-second resolution. */
export function openFlowBoarding(state: FlowState, facts: FlowFacts): void {
    const boarding = facts.departments.find(d => d.index === 6)!;
    const open = state.planes.some(p => p.phase === PHASE.BOARDING && p.boardingCalled && p.boarded < p.seats);
    for (const asker of [...state.inquiry]) {
        const walk = Math.min(asker.walkFrames, 60); asker.walkFrames -= walk;
        if (asker.walkFrames === 0 && boarding.workers) asker.answerFrames = Math.max(0, asker.answerFrames - (60 - walk));
        if (asker.walkFrames === 0 && asker.answerFrames === 0) {
            state.inquiry.splice(state.inquiry.indexOf(asker), 1);
            const back: FlowPassenger & Partial<InquiryPassenger> = asker; delete back.walkFrames; delete back.answerFrames; state.lounge.push(back);
        }
    }
    // While no boarding is open, one unasked fifth-serial lounge passenger at a time walks to the desk.
    if (!open && !state.inquiry.length) {
        const asker = state.lounge.find(p => p.serial % 5 === 0 && !p.inquired);
        if (asker) {
            state.lounge.splice(state.lounge.indexOf(asker), 1);
            state.inquiry.push(Object.assign(asker, { inquired: true, walkFrames: facts.inquiryWalkFrames, answerFrames: facts.inquiryFrames }));
        }
    }
    const question = state.inquiry.length > 0;
    // Readiness counts gate-stage queue, approach, served, lounge and question passengers, never Security walkers.
    const desk = state.departments[state.departments.length - 1];
    const ready = state.lounge.length + state.inquiry.length + desk.queued.length + desk.inService.length + desk.completed.length;
    for (const plane of state.planes) {
        const timed = plane;
        ensureTimetable(timed, state.frame);
        if (plane.phase !== PHASE.BOARDING) continue;
        const early = earlyBoardingWindowOpen(timed, state.frame) && ready > 0 && boarding.workers;
        if (!boardingWindowOpen(timed, state.frame) && !early) continue;
        if (plane.boardingAnnouncedFrame < 0) plane.boardingAnnouncedFrame = state.frame;
        if (!boarding.workers || question || state.frame < plane.aircraftReadyFrame) { plane.boardingDeskFrames = 0; continue; }
        plane.boardingDeskFrames = Math.min(BOARDING_DESK_FRAMES, plane.boardingDeskFrames + 60);
        if (!early && (state.frame - plane.boardingAnnouncedFrame < BOARDING_SHORTLY_FRAMES || plane.boardingDeskFrames < BOARDING_DESK_FRAMES)) continue;
        if (blockedByDeplaning(state, plane.gate)) continue;
        if (!plane.boardingCalled) { plane.boardingCalled = true; plane.boardingStartedFrame = state.frame; }
    }
    if (state.planes.some(p => p.phase === PHASE.BOARDING && p.boardingCalled && p.boarded < p.seats)) {
        desk.queued.push(...state.lounge); state.lounge = [];
    }
}

/** Visiting flights only: departures, cruise, approach, landing loads and turnaround, with the live random draws. */
export function advanceFlowFlights(state: FlowState, facts: FlowFacts): void {
    for (const p of state.planes) {
        if (p.phase === PHASE.BOARDING) {
            if (blockedByDeplaning(state, p.gate)) continue;
            if (p.boardingCalled && p.boarded >= p.seats) { if (p.departureReadyFrame < 0) p.departureReadyFrame = state.frame + DEPARTURE_COUNTDOWN; }
            else p.departureReadyFrame = -1;
            if (!p.boardingCalled || !p.boarded) continue;
            if (p.boarded >= p.seats && state.frame < p.departureReadyFrame) continue;
            if (p.boarded < p.seats && state.frame < Math.max(p.scheduledDepartureFrame, p.boardingStartedFrame) + facts.tier.boardingWindow) continue;
            if (state.reservations.some(r => r.gate === p.gate)) continue;
            p.phase = PHASE.TAXI; p.clock = 0;
            p.tripFrames = departureTripFrames(facts.tier.turnaround, state.frame - p.scheduledDepartureFrame, true);
            recordDeparture(p, state.frame, facts.run > 1 && randomInteger(state, 0, 9) < 2 ? (randomInteger(state, 0, 1) ? 3 : -2) * AIRPORT_MINUTE : 0);
            state.flights++; continue;
        }
        p.clock += 60;
        if (p.phase === PHASE.TAXI && p.clock >= GROUND_FRAMES) { p.phase = PHASE.AIRBORNE; p.clock = 0; }
        else if (p.phase === PHASE.AIRBORNE && state.frame >= p.expectedArrivalFrame - GROUND_FRAMES) { p.phase = PHASE.APPROACH; p.clock = 0; }
        else if (p.phase === PHASE.APPROACH && p.clock >= GROUND_FRAMES) {
            p.arrivalOffset = state.frame - p.scheduledArrivalFrame;
            const outgoing = state.lounge.length + state.inquiry.length + state.transit.length + state.reservations.length + state.departments.reduce((sum, d) => sum + d.queued.length + d.inService.length + d.completed.length, 0);
            const count = Math.min(p.seats - randomInteger(state, 0, Math.floor(p.seats / 3)), Math.max(0, facts.maxPassengers - outgoing - state.inbound.length));
            for (let n = 0; n < count; n++) {
                const serial = ++state.serial; randomInteger(state, 0, 5);
                state.inbound.push({ serial, massMilli: 1000, gate: p.gate, gateFrames: n * 48 + facts.deplaneFrames[p.gate], remainingFrames: 6000 + n * 48 }); state.landedMilli += 1000;
            }
            Object.assign(p, { phase: PHASE.BOARDING, boardingCalled: false, clock: 0, boarded: 0, flightNumber: p.flightNumber + 1, tripFrames: facts.tier.turnaround });
            scheduleTurnaround(p, state.frame);
            if (facts.run > 1 && (p.flightNumber + p.gate) % 5 === 0) p.aircraftReadyFrame = p.scheduledDepartureFrame + 3 * AIRPORT_MINUTE;
        }
    }
    for (const inbound of [...state.inbound]) {
        inbound.remainingFrames -= 60; inbound.gateFrames = Math.max(0, inbound.gateFrames - 60);
        if (inbound.remainingFrames <= 0) { state.inbound.splice(state.inbound.indexOf(inbound), 1); state.exitedMilli += inbound.massMilli; }
    }
}
